import { Cart } from "./cart";
import { CartItem, ServiceCartItem, ServiceCartValue } from "./types";
import { LocalStorageKey } from "@/utils/storageKeys";
import { CartType } from "@/constants/enum";

/**
 * Cart implementation specifically for the Service vertical.
 *
 * Handles logic unique to service bookings, such as appointment times,
 * employee selection, and service duration.
 *
 * @extends {Cart<ServiceCartItem>}
 */
export class ServiceCart extends Cart<ServiceCartItem> {
  /** The selected time for the appointment. */
  public appointmentTime: Date | null;
  /** The ID of the employee performing the service (optional). */
  public employeeId: string;
  /** The total duration of the service in minutes. */
  public serviceDuration: number;

  /**
   * Creates a new ServiceCart instance.
   *
   * @param data - Optional initial data.
   */
  constructor(data?: any) {
    super(data);
    this.appointmentTime = data?.appointmentTime
      ? new Date(data.appointmentTime)
      : null;
    this.employeeId = data?.employeeId || "";
    this.serviceDuration = data?.serviceDuration || 0;
  }

  // Example: Add method for managing appointment details
  /**
   * Updates the appointment details for the service cart.
   *
   * @param time - The new appointment time.
   * @param employeeId - The ID of the selected employee.
   * @param duration - The duration of the service.
   */
  setAppointmentDetails(
    time: Date,
    employeeId: string,
    duration: number
  ): void {
    this.appointmentTime = time;
    this.employeeId = employeeId;
    this.serviceDuration = duration;
  }

  // Override toJSON to include the type discriminator and specific properties
  toJSON(): any {
    return {
      ...super.toJSON(),
      appointmentTime: this.appointmentTime?.toISOString() || null,
      employeeId: this.employeeId,
      serviceDuration: this.serviceDuration,
      type: CartType.SERVICE_CART,
    };
  }

  copyPropertiesFrom(other: Cart): void {
    super.copyPropertiesFrom(other);
    if (other instanceof ServiceCart) {
      this.appointmentTime = other.appointmentTime;
      this.employeeId = other.employeeId;
      this.serviceDuration = other.serviceDuration;
    }
  }

  // Static method to re-hydrate from a plain object
  static fromJSON(data: any): ServiceCart {
    return new ServiceCart(data);
  }

  static addItem(item: ServiceCartValue): void {
    const cartItem: CartItem = {
      key: item.cartId,
      value: item,
    } as CartItem;
    this._addToStorage(LocalStorageKey.SERVICE_CART, cartItem);
  }

  static getItems(): ServiceCartItem[] {
    return this._getFromStorage<ServiceCartItem>(LocalStorageKey.SERVICE_CART);
  }

  static clear(): void {
    this._clearStorage(LocalStorageKey.SERVICE_CART);
  }

  static removeItem(cartId: string): void {
    this._removeFromStorage(LocalStorageKey.SERVICE_CART, cartId);
  }
}
